import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StarMoneyFacadeService } from '../core';
import { HttpErrorResponse } from '@angular/common/http';
import { SharedDataService } from './shared-data.service';

export enum StateStatus {
  SUCCESS = 'success',
  ACTIVE = 'active',
  WAITING = 'waiting',
  REJECTED = 'rejected',
}

export interface StepDetail {
  level: number;
  label: string;
  description: string;
  status: StateStatus;
}

export interface Step {
  step: number;
  title: string;
  label: string;
  status: StateStatus;
  details: StepDetail[];
}

type FlowObject = {
  [key: string]: { [key: string]: any };
};

type ComponentMap = {
  [key: number]: any;
};

@Injectable({
  providedIn: 'root',
})
export class WorkFlowService {
  private flowObjectSubject = new BehaviorSubject<FlowObject | null>(null);
  public flowObject$: Observable<FlowObject | null> = this.flowObjectSubject.asObservable();
  private currentStepIndex: number = 0;
  public freeze_step: boolean = false;
  public max_loan_step: number = 0;
  public activeDetailIndex: number = 0;
  // info
  public txn_id: string | null = null;
  public checkPointPageID: number = 19; //main guideline page
  public txn_expiry_at: number;

  set txnId(txn_id: string | null) {
    this.txn_id = txn_id;
  }

  get txnId(): string | null {
    return this.txn_id;
  }
  // Lazy load components using dynamic imports
  private pageIdToComponentMap: { [key: number]: () => Promise<any> } = {
    1: () => import('../pages/term-condition/term-condition.page').then(m => m.TermConditionPage),
    2: () => import('../pages/consent/consent.page').then(m => m.ConsentPage),
    3: () => import('../pages/landing/landing.page').then(m => m.LandingPage),
    4: () => import('../pages/guidelines/ocr-guideline/ocr-guideline.page').then(m => m.OcrGuidelinePage),
    5: () => import('../pages/confirm-img/confirm-img.page').then(m => m.ConfirmImgPage),
    6: () => import('../pages/id-card-info/id-card-info.page').then(m => m.IdCardInfoPage),
    7: () => import('../pages/phone-number/phone-number.page').then(m => m.PhoneNumberPage),
    8: () => import('../pages/guidelines/face-guideline/face-guideline.page').then(m => m.FaceGuidelinePage),
    9: () => import('../pages/processing/processing.page').then(m => m.ProcessingPage),
    10: () => import('../pages/success/success.page').then(m => m.SuccessPage),
    12: () => import('../pages/face-preview/face-preview.page').then(m => m.FacePreviewPage),
    13: () => import('../pages/confirm-otp/confirm-otp.page').then(m => m.ConfirmOtpPage),
    14: () => import('../pages/thai-d/thai-d.page').then(m => m.ThaiDPage),
    15: () => import('../pages/email/email.page').then(m => m.EmailPage),
    16: () => import('../pages/confirm-email/confirm-email.page').then(m => m.ConfirmEmailPage),
    17: () => import('../pages/device-check/device-check.page').then(m => m.DeviceCheckPage),
    19: () => import('../pages/guidelines/main-guideline/main-guideline.page').then(m => m.MainGuidelinePage),
    20: () => import('../pages/select-loan-type/select-loan-type.page').then(m => m.SelectLoanTypePage),
    21: () => import('../features/document-detection/thai-id-front-pico/pages/capture/capture.page').then(m => m.ThaiIdFrontPicoCapturePage),
    38: () => import('../features/document-detection/thai-id-back/pages/capture/capture-thai-id-back.page').then(m => m.ThaiIdBackCapturePage),
    // 22: () => import('../pages/face-capture/face-capture.page').then(m => m.FaceCapturePage),
    22: () => import('../pages/take-face-detection-v2/take-face-detection-v2.component').then(m => m.TakeFaceDetectionV2Component),
    23: () => import('../pages/loading/loading.page').then(m => m.LoadingPage),
    24: () => import('../pages/loan-info/loan-info.page').then(m => m.LoanInfoPage),
    25: () => import('../pages/fail/fail.page').then(m => m.FailPage),
    26: () => import('../pages/thai-d/thai-d-pending/thai-d-pending.page').then(m => m.ThaiDPendingPage),
    27: () => import('../pages/address/address.page').then(m => m.AddressPage),
    28: () => import('../pages/working-info/working-info.page').then(m => m.WorkingInfoPage),
    29: () => import('../pages/select-installment-product/select-installment-product.page').then(m => m.SelectInstallmentProductPage),
    30: () => import('../pages/attach-evidence/attach-evidence.page').then(m => m.AttachEvidencePage),
    31: () => import('../pages/product-info/product-info.page').then(m => m.ProductInfoPage),
    32: () => import('../pages/device-preparation/device-preparation.page').then(m => m.DevicePreparationPage),
    33: () => import('../pages/device-failed/device-failed.page').then(m => m.DeviceFailedPage),
    34: () => import('../pages/select-verify-method/select-verify-method.page').then(m => m.SelectVerifyMethodPage),
    35: () => import('../pages/landing-resume-stage/landing-resume-stage.page').then(m => m.LandingResumeStagePage),
    36: () => import('../pages/browser-check/browser-check.page').then(m => m.BrowserCheckPage),
    37: () => import('../pages/cancel-register/cancel-register.page').then(m => m.CancelRegisterPage)
  };

  public steps: Step[] = [
    {
      step: 1,
      title: 'เลือก<br>ผลิตภัณฑ์',
      label: 'เลือกผลิตภัณฑ์',
      status: StateStatus.ACTIVE,
      details: [
        {
          level: 1.1,
          label: 'ข้อกำหนดและเงื่อนไขการใช้บริการสตาร์ มันนี่ สมาร์ทโลน',
          description:
            'ศึกษา ทำความเข้าใจ และให้การยอมรับ ข้อกำหนดและเงื่อนไขการใช้บริการ เพื่อดำเนินการต่อ',
          status: StateStatus.ACTIVE,
        },
        {
          level: 1.2,
          label: 'เลือกผลิตภัณฑ์สินเชื่อ',
          description:
            'ศึกษา ทำความเข้าใจรายละเอียดของผลิตภัณฑ์ที่ท่านสนใจ ก่อนเริ่มต้นทำการสมัครสินเชื่อ',
          status: StateStatus.WAITING,
        },
      ],
    },
    {
      step: 2,
      title: 'ข้อมูล<br>การติดต่อ',
      label: 'ข้อมูลการติดต่อ',
      status: StateStatus.WAITING,
      details: [
        {
          level: 2.1,
          label: 'ลงทะเบียนเบอร์โทรศัพท์มือถือ',
          description: 'ระบุหมายเลขโทรศัพท์ของท่าน และทำยืนยันรหัส OTP ที่ได้จากระบบ',
          status: StateStatus.WAITING,
        },
        {
          level: 2.2,
          label: 'ลงทะเบียนอีเมลแอดเดรส (ถ้ามี)',
          description: 'ระบุอีเมลแอดเดรสของท่าน และทำยืนยันรหัส OTP ที่ได้จากระบบ',
          status: StateStatus.WAITING,
        },
        {
          level: 2.3,
          label: 'ให้ความยินยอม การเก็บและใช้ข้อมูลส่วนบุคคล',
          description:
            'เลือกช่องทางที่ท่านยินยอมให้บริษัทฯ ทำการติดต่อท่านได้ และ เลือกผลิตภัณฑ์ที่ท่านสนใจรับข่าวสารหรือโปรโมชัน',
          status: StateStatus.WAITING,
        },
      ],
    },
    {
      step: 3,
      title: 'ข้อมูล<br>อัตลักษณ์บุคคล',
      label: 'ข้อมูลอัตลักษณ์บุคคล',
      status: StateStatus.WAITING,
      details: [
        {
          level: 3.1,
          label: 'ให้ความยินยอม การเก็บและใช้ข้อมูลชีวมิติ',
          description: 'ศึกษา ทำความเข้าใจรายละเอียด และให้ความยินยอมเพื่อดำเนินการต่อ',
          status: StateStatus.WAITING,
        },
        {
          level: 3.2,
          label: 'ถ่ายรูปบัตรประชาชน',
          description:
            'ถ่ายรูปบัตรประชาชน ด้านหน้า และด้านหลัง และทำการตรวจสอบความถูกต้องของข้อมูลของท่านก่อนทำการตรวจสอบกับหน่วยงานที่เกี่ยวข้อง',
          status: StateStatus.WAITING,
        },
        {
          level: 3.3,
          label: 'ถ่ายรูปใบหน้า',
          description: 'ถ่ายรูปใบหน้าของท่าน เพื่อยืนยันความเป็นเจ้าของข้อมูล',
          status: StateStatus.WAITING,
        },
      ],
    },
    {
      step: 4,
      title: 'ข้อมูล<br>สมัครสินเชื่อ',
      label: 'ข้อมูลสมัครสินเชื่อ',
      status: StateStatus.WAITING,
      details: [
        {
          level: 4.1,
          label: 'กรอกข้อมูลการสมัครสินเชื่อ',
          description:
            'กรอกข้อมูลการสมัครสินเชื่อให้ครบถ้วน และทำการถ่ายภาพ หรือแนบเอกสารที่เกี่ยวข้อง เพื่อใช้ประกอบในการพิจารณาสินเชื่อ',
          status: StateStatus.WAITING,
        },
      ],
    },
    {
      step: 5,
      title: 'ยืนยันตัวตน<br>ด้วย ThaiD',
      label: 'ยืนยันตัวตนด้วย ThaiD',
      status: StateStatus.WAITING,
      details: [
        {
          level: 5.1,
          label: 'ให้ความยินยอมเปิดเผยข้อมูลโดยวิธีการผ่าน อินเทอร์เน็ต ',
          description: 'ศึกษา ทำความเข้าใจรายละเอียด และให้ความยินยอมเพื่อดำเนินการต่อ',
          status: StateStatus.WAITING,
        },
        {
          level: 5.2,
          label:
            'เงื่อนไขข้อตกลงกรณีมีการใช้บริการเสริมระบบเชื่อมต่อ DOPA Digital ID ที่ NDID จัดให้มีขึ้น',
          description: 'ศึกษา ทำความเข้าใจรายละเอียด และให้ความยินยอมเพื่อดำเนินการต่อ',
          status: StateStatus.WAITING,
        },
        {
          level: 5.3,
          label: 'ยืนยันรหัส OTP',
          description:
            'ยืนยันรหัส OTP ที่ส่งไปยังเบอร์โทรศัพท์ที่ลงทะเบียนไว้ เพื่อยืนยันการยิยอมให้เปิดเผยข้อมูล',
          status: StateStatus.WAITING,
        },
        {
          level: 5.4,
          label: 'ยืนยันตัวตนด้วย ThaiD และผลการยืนยันตัวตน',
          description:
            'เปิดแอปพลิเคชัน ThaiD เพื่อดำเนินการต่อ หลังจากยืนยันตัวตนสำเร็จ ระบบจะแจ้งเลขที่ใบสมัครสินเชื่อให้ท่านทราบ',
          status: StateStatus.WAITING,
        },
      ],
    },
  ];

  public loan_steps: any = [
    {
      step: 1,
      title: 'ข้อมูล<br>ที่อยู่',
      status: StateStatus.ACTIVE,
    },
    {
      step: 2,
      title: 'ข้อมูล<br>การทำงาน',
      status: StateStatus.WAITING,
    },
    {
      step: 3,
      title: 'เลือกสินค้าและ<br>การผ่อนชำระ',
      status: StateStatus.WAITING,
    },
    {
      step: 4,
      title: 'แนบเอกสาร<br>หลักฐาน',
      status: StateStatus.WAITING,
    },
    {
      step: 5,
      title: 'ตรวจสอบ<br>ข้อมูล',
      status: StateStatus.WAITING,
    },
  ];


  private loanStepsSubject = new BehaviorSubject<any[]>(this.loan_steps)
  public loanSteps$ = this.loanStepsSubject.asObservable();

  constructor(
    private starMoneyFacadeService: StarMoneyFacadeService,
    private sharedDataService: SharedDataService
  ) { }

  private async loadComponent(pageId: number): Promise<any> {
    const loader = this.pageIdToComponentMap[pageId];
    if (!loader) {
      console.warn(`No component found for page_id ${pageId}`);
      return null;
    }
    return loader();
  }

  async initializeWorkflow(backendData: any) {
    const flowObject = await this.generateFlowObject(backendData);
    this.flowObjectSubject.next(flowObject);
  }

  async generateFlowObject(
    backendData: {
      step: string;
      state: string;
      pages: { page_id: number; step: string; can_resume?: boolean; params?: any; sub_flows?: any }[];
    }[]
  ): Promise<FlowObject> {
    const flowObject: FlowObject = {};

    for (const { step, state, pages } of backendData) {
      flowObject[step] = [];

      const stepPages = await Promise.all(
        pages.map(async (page) => {
          return {
            step_key: page?.step || step,
            params: page.params || {},
            page_id: page.page_id,
            state,
            can_resume: page?.can_resume ?? true,
            sub_flow: page.sub_flows ? await this.generateSubFlow(page.sub_flows) : null,
          };
        })
      );

      // Assign only valid objects (remove null entries)
      flowObject[step] = stepPages.filter((p) => p !== null);
    }

    return flowObject;
  }


  async generateSubFlow(subFlow: any[]) {
    return Promise.all(
      subFlow.map(async (sub, index) => {
        const { pages, state, step } = sub;

        const stepPages = await Promise.all(
          pages.map(async (page: any) => {
            return {
              step_key: page?.step || step,
              params: page.params || {},
              page_id: page.page_id,
              state,
              can_resume: page?.can_resume ?? true,
              sub_flow: page.sub_flows ? await this.generateSubFlow(page.sub_flows) : null,
            };
          })
        );

        return {
          step_key: step || `sub_${index}`,
          state,
          pages: stepPages.filter((p) => p !== null), // Remove null entries
        };
      })
    );
  }


  async getPageIdByComponent(componentName: string): Promise<number | null> {
    for (const [id, loadComponent] of Object.entries(this.pageIdToComponentMap)) {
      const component = await loadComponent();
      if (component.name === componentName) {
        return parseInt(id, 10);
      }
    }
    return null;
  }


  async getPageComponentByComponentName(componentName: string) {
    for (const [pageId, loadComponent] of Object.entries(this.pageIdToComponentMap)) {
      const component = await loadComponent(); // Resolve the Promise to get the actual component

      if (component.name === componentName) {
        return component;
      }
    }
    return null; // Return null if no match is found
  }

  async getPageComponentByID(id: number) {
    const component = await this.loadComponent(id);
    return component;
  }


  /**
   * อัพเดทขั้นตอนปัจจุบัน
   */

  getCurrentStep() {
    if (this.currentStepIndex === 0) {
      this.currentStepIndex += 1;
    }
    if (this.currentStepIndex > this.steps.length) {
      this.freezeLastStep();
    }
    return this.currentStepIndex;
  }


  async updateCurrentStepIDAndPageID(
    step_id: string,
    page_id: number,
  ): Promise<void> {
    const applicationId =
      this.sharedDataService.getData('application_id');
    if (!this.txn_id || (page_id === 26 && !applicationId)) return;
    try {
      await this.starMoneyFacadeService.updateCurrentStep({
        txn_id: this.txn_id,
        page_id,
        step_id,
      });
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        console.error('HTTP Error:', error.error?.message);
        return;
      }

      if (error instanceof Error) {
        console.error('JS Error:', error.message);
      }
    }
  }
  //------------------------------------------------------------------------------------------------
  //------------------------------------for mock step run-------------------------------------------
  //--------------------------------------------------------------------------------------------------

  setCurrentStepIndex(index: number) {
    this.currentStepIndex = index;
  }

  async updateCurrentStep() {
    //TODO: Maybe move to backend
    const notHaveRejected = this.checkRejectedState();
    if (!this.freeze_step && notHaveRejected) {
      if (this.currentStepIndex != 0) {
        this.stampSuccessState(this.currentStepIndex - 1);
      }
      this.currentStepIndex += 1;
    }
  }

  checkRejectedState() {
    if (this.currentStepIndex != 0) {
      const step = this.steps[this.currentStepIndex - 1];
      return step.status === StateStatus.REJECTED ? false : true;
    }
    return true;
  }

  stampSuccessState(index: number) {
    const step = this.steps[index];
    if (!step) return;

    step.status = StateStatus.SUCCESS;
    step.details.forEach((detail) => {
      detail.status = StateStatus.SUCCESS;
    });

    this.activateNextStep(index);
  }

  async stampRejected(current_page_index: number) {
    const step = this.steps[this.currentStepIndex - 1];
    step.details[current_page_index].status = StateStatus.REJECTED;
  }

  private activateNextStep(currentIndex: number): void {
    const nextStep = this.steps[currentIndex + 1];
    if (nextStep) {
      nextStep.status = StateStatus.ACTIVE;
      nextStep.details[0].status = StateStatus.ACTIVE;
    }
  }

  async updatePassStep(
    previous_flow_index: number,
    max_flow_index: number
  ) {
    if (
      previous_flow_index > 0 &&
      previous_flow_index >= max_flow_index &&
      this.currentStepIndex == max_flow_index
    ) {
      await this.updateCurrentStep();
    }
  }

  resetSteps() {
    this.currentStepIndex = 0;
    // this.steps.forEach((step: any) => {
    //     step.is_passed = false;
    // });
  }

  freezeLastStep() {
    this.freeze_step = true;
    this.currentStepIndex = this.steps.length;
    this.stampSuccessState(this.currentStepIndex);
  }

  updateLoanSteps(index: number, status: string) {
    if (this.loan_steps[index].status != StateStatus.SUCCESS) {
      this.loan_steps[index].status = status;
      if (status === StateStatus.SUCCESS && index + 1 < this.loan_steps.length) {
        this.loan_steps[index + 1].status = StateStatus.ACTIVE;
      }
      if (this.max_loan_step < index) {
        this.max_loan_step = index;
      }
      this.loanStepsSubject.next(this.loan_steps);
    }
  }

  resetLoanSteps(activeIndex: number) {
    this.loan_steps = this.loan_steps.map((step: any, index: number) => ({
      ...step,
      status: index < activeIndex ? StateStatus.SUCCESS : index === activeIndex ? StateStatus.ACTIVE : StateStatus.WAITING
    }));

    this.loanStepsSubject.next(this.loan_steps);
  }


  async updateStepStatusWithParam(stepNumber: number, levelNumber?: number, is_last: boolean = false) {
    for (const step of this.steps) {
      if (step.step < stepNumber) {
        // Mark all previous steps as SUCCESS
        step.status = StateStatus.SUCCESS;
        step.details.forEach((detail) => (detail.status = StateStatus.SUCCESS));
      } else if (step.step === stepNumber && levelNumber) {

        step.status = StateStatus.ACTIVE;
        // Mark details up to levelNumber as SUCCESS
        for (let i = 0; i < step.details.length; i++) {
          const nextLevel = parseFloat((Number(levelNumber) + 0.1).toFixed(1));
          const detail = step.details[i];

          if (detail.level < levelNumber) {
            detail.status = StateStatus.SUCCESS;
          } else if (detail.level === levelNumber) {
            if (is_last) {
              detail.status = StateStatus.SUCCESS;
            } else {
              detail.status = StateStatus.ACTIVE;
            }
          } else if (detail.level == nextLevel && is_last) {
            detail.status = StateStatus.ACTIVE;
            this.activeDetailIndex = i; // Store active detail index
            break;
          }
          // ✅ If the last detail is also marked as SUCCESS, update step to SUCCESS
          const lastDetail = step.details[step.details.length - 1];
          if (lastDetail.level === levelNumber && is_last) {
            step.status = StateStatus.SUCCESS;
          }

          // ✅ Mark next step as ACTIVE
          const nextStep = this.steps.find((s) => s.step === stepNumber + 1);
          if (nextStep) {
            nextStep.status = StateStatus.ACTIVE;
            nextStep.details[0].status = StateStatus.ACTIVE;
          }
        }
      } else {
        // Keep remaining steps unchanged
        break;
      }
    }
    this.setCurrentStepIndex(stepNumber);

  }


}
